import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  type EngagementOutput,
  type NoteOutput,
  type TimeEntryOutput,
  type EngagementSubItemOutput,
  type EngagementSubItemStatus,
  type FreeZoneOutput,
  type UpdateEngagementInput,
  type UserOutput,
  JURISDICTION_LABELS,
  ENGAGEMENT_PIPELINE_LABELS,
  ENGAGEMENT_SUB_ITEM_STATUS_LABELS,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch, captureAuthenticatedFetch } from '../lib/auth-context.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { ApplyTemplateModal } from '../components/ApplyTemplateModal.js';
import { NoteEditorModal } from '../components/NoteEditorModal.js';
import { NotebookPen } from 'lucide-react';
import { httpError, isHttpStatus } from '../lib/http-error.js';
import { ForbiddenPage } from '../components/ForbiddenPage.js';
import { EntityTags } from '../components/EntityTags.js';
import { EngagementFormModal, ConfirmModal, applyCatalogChecklist, type CatalogApply } from './EngagementsPage.js';
import { fmtDate } from '../lib/date.js';

type Tab = 'overview' | 'sub-items' | 'notes' | 'time';

export function EngagementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [applyResult, setApplyResult] = useState<string | null>(null);
  const { canDo } = useCapabilities();

  const canManage = canDo('engagements.edit');

  const update = useMutation({
    mutationFn: async ({ patch, catalog }: { patch: UpdateEngagementInput; catalog: CatalogApply | null }) => {
      const send = captureAuthenticatedFetch();
      const res = await send(`/v1/engagements/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      if (catalog) {
        try {
          return await applyCatalogChecklist(id!, catalog, send);
        } catch (err) {
          return `Engagement saved, but the checklist apply failed: ${(err as Error).message}`;
        }
      }
      return null;
    },
    onSuccess: (catalogNote) => {
      queryClient.invalidateQueries({ queryKey: ['engagement', id] });
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      queryClient.invalidateQueries({ queryKey: ['my-engagements'] });
      queryClient.invalidateQueries({ queryKey: ['engagement-sub-items', id] });
      setShowEdit(false);
      if (catalogNote) setApplyResult(catalogNote);
    },
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/engagements/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagements'] });
      queryClient.invalidateQueries({ queryKey: ['my-engagements'] });
      navigate('/engagements');
    },
  });

  const engagement = useQuery({
    queryKey: ['engagement', id],
    enabled: !!id && api !== null,
    queryFn: async () => {
      const res = await fetch(`/v1/engagements/${id}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: EngagementOutput };
      return body.data;
    },
  });

  const notes = useQuery({
    queryKey: ['notes', { scope: 'ENGAGEMENT', scopeId: id }],
    enabled: tab === 'notes' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/notes?scope=ENGAGEMENT&scopeId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: NoteOutput[] } };
      return body.data.items;
    },
  });

  const timeEntries = useQuery({
    queryKey: ['time-entries', { engagementId: id }],
    enabled: tab === 'time' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/time-entries?engagementId=${id}&limit=100`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: TimeEntryOutput[]; total: number } };
      return body.data;
    },
  });

  const subItems = useQuery({
    queryKey: ['engagement-sub-items', id],
    enabled: tab === 'sub-items' && !!id,
    queryFn: async () => {
      const res = await fetch(`/v1/engagements/${id}/sub-items`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: EngagementSubItemOutput[] } };
      return body.data.items;
    },
  });

  const freeZones = useQuery({
    queryKey: ['free-zones', 'engagement'],
    enabled: tab === 'sub-items',
    queryFn: async () => {
      const res = await fetch('/v1/free-zones', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: FreeZoneOutput[] } };
      return body.data.items;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: ['overview', 'notes', 'time'].includes(tab),
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const client = useQuery({
    queryKey: ['client', engagement.data?.clientId],
    enabled: !!engagement.data?.clientId,
    queryFn: async () => {
      const res = await fetch(`/v1/clients/${engagement.data!.clientId}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { id: string; name: string; company: string | null; status: string; retainerDecision?: 'APPLIED' | 'DEFERRED' | null } };
      return body.data;
    },
  });

  // OPS 3.7 step 2 fork: checklist complete on a signed engagement → prompt
  // to start the compliance retainer (apply a template → client ACTIVE) or
  // defer (client INACTIVE). "Later" just dismisses — the client page banner
  // keeps the decision open.
  const [showFork, setShowFork] = useState(false);
  const [applyFromFork, setApplyFromFork] = useState(false);
  const clientStatus = useMutation({
    mutationFn: async ({ clientId, engagementId }: { clientId: string; engagementId: string }) => {
      const res = await fetch(`/v1/clients/${clientId}/retainer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ decision: 'DEFERRED', engagementId }),
      });
      if (!res.ok) throw await httpError(res);
      return (await res.json() as { data: { clientStatus: string; retainerDecision: 'DEFERRED' } }).data;
    },
    onSuccess: (result, v) => {
      queryClient.setQueryData(['client', v.clientId], (current: object | undefined) => current ? { ...current, status: result.clientStatus, retainerDecision: result.retainerDecision, setupAwaitingRetainer: false } : current);
      queryClient.invalidateQueries({ queryKey: ['client', v.clientId] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setShowFork(false);
    },
  });

  const [showNoteEditor, setShowNoteEditor] = useState(false);

  const createNote = useMutation({
    mutationFn: async (body: { title?: string; bodyMarkdown: string }) => {
      const res = await fetch('/v1/notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
        },
        body: JSON.stringify({ scope: 'ENGAGEMENT', scopeId: id, ...body }),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', { scope: 'ENGAGEMENT', scopeId: id }] });
      queryClient.invalidateQueries({ queryKey: ['client-notes'] });
    },
  });

  function userName(uid: string | null) {
    if (!uid) return '—';
    const u = users.data?.find((x) => x.id === uid);
    return u ? `${u.displayName} (${u.email})` : uid.slice(0, 8);
  }

  if (engagement.isLoading) return <div className="text-muted p-8 text-center">Loading engagement…</div>;
  if (engagement.error && isHttpStatus(engagement.error, 403, 404)) {
    return <ForbiddenPage title="Not found or no access" message="This engagement doesn't exist or you don't have permission to view it." />;
  }
  if (engagement.error) return <div className="text-rose-400 p-8 text-center">{(engagement.error as Error).message}</div>;
  if (!engagement.data) return <div className="text-muted p-8 text-center">Engagement not found.</div>;

  const e = engagement.data;

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/engagements')} className="text-xs text-muted hover:text-white mb-1">← Back to Engagements</button>
          <h1 className="text-xl font-semibold text-white">{e.title ?? 'Untitled engagement'}</h1>
          <p className="text-sm text-muted">
            {client.data ? (
              <Link to={`/clients/${e.clientId}`} className="hover:text-accent underline">{client.data.company ?? client.data.name}</Link>
            ) : e.clientId}
            {' · '}
            {e.jurisdiction ? (JURISDICTION_LABELS[e.jurisdiction as keyof typeof JURISDICTION_LABELS] ?? e.jurisdiction) : '—'}
            {' · '}
            <StatusPill pipeline={e.pipeline} />
          </p>
          <div className="mt-2">
            <EntityTags scope="ENGAGEMENT" entityId={e.id} />
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="text-right">
            <p className="text-sm text-white">{e.feeAmount ? `${(e.feeAmount / 100).toLocaleString()} ${e.feeCurrency}` : '—'}</p>
          </div>
          {(canManage || canDo('engagements.delete') || (canDo('templates.view') && canDo('compliance.edit'))) && (
            <div className="flex gap-2">
              {canDo('templates.view') && canDo('compliance.edit') && (
                <button
                  onClick={() => { setApplyResult(null); setShowApplyTemplate(true); }}
                  className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
                >
                  Apply template
                </button>
              )}
              <button
                disabled={!canDo('engagements.edit')}
                onClick={() => setShowEdit(true)}
                className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
              >
                Edit
              </button>
              <button
                disabled={!canDo('engagements.delete')}
                onClick={() => setShowDelete(true)}
                className="text-sm btn-ghost-danger border px-3 py-1.5 rounded-lg"
              >
                Delete
              </button>
            </div>
          )}
        </div>
      </header>

      {applyResult && (
        <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 flex items-center justify-between">
          <span>{applyResult}</span>
          <button onClick={() => setApplyResult(null)} className="text-emerald-300/70 hover:text-emerald-200 ml-3">✕</button>
        </div>
      )}

      <div className="border-b border-border">
        <nav className="flex gap-1">
          {(['overview', 'sub-items', 'notes', 'time'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium capitalize ${tab === t ? 'text-accent border-b-2 border-accent' : 'text-muted hover:text-white'}`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="bg-bg-panel border border-border rounded-xl p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Start" value={e.startDate ? fmtDate(e.startDate) : '—'} />
            <Field label="End" value={e.endDate ? fmtDate(e.endDate) : '—'} />
            <Field label="Manager" value={userName(e.managerId)} />
            <Field label="Won at" value={e.retainerWonAt ? fmtDate(e.retainerWonAt) : '—'} />
          </div>
        </div>
      )}

      {tab === 'sub-items' && (
        <SubItemsSection
          engagementId={id!}
          canManage={canManage}
          canAddNote={canDo('engagements.edit')}
          subItems={subItems.data ?? []}
          isLoading={subItems.isLoading}
          error={subItems.error as Error | null}
          freeZones={freeZones.data ?? []}
          onToast={setApplyResult}
          onAllCompleted={() => {
            if (e.pipeline === 'ENGAGED' && client.data?.status === 'LEAD' && !client.data.retainerDecision) setShowFork(true);
          }}
        />
      )}

      {tab === 'notes' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setShowNoteEditor(true)}
              aria-label="Add engagement note"
              className="inline-flex items-center gap-1.5 bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              <NotebookPen size={14} /> Add note
            </button>
          </div>
          <DataList
            isLoading={notes.isLoading}
            error={notes.error as Error | null}
            items={notes.data ?? []}
            empty="No notes yet."
            render={(n) => (
              <div className="border-b border-border/50 last:border-0 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{n.title ?? 'Note'}</span>
                  <span className="text-xs text-muted">{fmtDate(n.createdAt)} · {userName(n.authorId)}</span>
                </div>
                {n.engagementSubItemTitle && (
                  <div className="text-xs text-accent mb-1">Checklist item: {n.engagementSubItemTitle}</div>
                )}
                <p className="text-sm text-white whitespace-pre-wrap">{n.bodyMarkdown}</p>
              </div>
            )}
          />
          {showNoteEditor && (
            <NoteEditorModal
              heading="Add engagement note"
              onClose={() => setShowNoteEditor(false)}
              onSave={async (input) => { await createNote.mutateAsync(input); }}
            />
          )}
        </div>
      )}

      {tab === 'time' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-white">Time entries</h2>
            <p className="text-xs text-muted">Log time from the global Time module.</p>
          </div>
          <DataList
            isLoading={timeEntries.isLoading}
            error={timeEntries.error as Error | null}
            items={timeEntries.data?.items ?? []}
            empty="No time logged yet."
            render={(t) => (
              <div className="border-b border-border/50 last:border-0 px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm text-white">{t.hours.toFixed(2)}h · {t.description ?? 'No description'}</p>
                  <p className="text-xs text-muted">{fmtDate(t.date)} · {userName(t.userId)} · {t.billable ? 'Billable' : 'Non-billable'}{t.invoiceId ? ' · Invoiced' : ''}</p>
                </div>
              </div>
            )}
          />
        </div>
      )}

      {showApplyTemplate && (
        <ApplyTemplateModal
          clientId={e.clientId}
          engagementId={e.id}
          retainerFork={applyFromFork}
          jurisdiction={e.jurisdiction ?? undefined}
          onClose={() => { setShowApplyTemplate(false); setApplyFromFork(false); }}
          onDone={(result) => {
            setShowApplyTemplate(false);
            setApplyFromFork(false);
            setApplyResult(`Template applied — created ${result.created}, skipped ${result.skipped}.`);
            if (result.clientStatus) queryClient.setQueryData(['client', e.clientId], (current: object | undefined) => current ? { ...current, status: result.clientStatus, retainerDecision: result.retainerDecision, setupAwaitingRetainer: result.retainerDecision ? false : undefined } : current);
            queryClient.invalidateQueries({ queryKey: ['client', e.clientId] });
            queryClient.invalidateQueries({ queryKey: ['clients'] });
            queryClient.invalidateQueries({ queryKey: ['engagement', id] });
            queryClient.invalidateQueries({ queryKey: ['compliance-items'] });
          }}
        />
      )}
      {showFork && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-bg-panel border border-border rounded-xl p-6 w-full max-w-md">
            <h2 className="text-base font-semibold text-white mb-2">Start compliance retainer?</h2>
            <p className="text-sm text-muted mb-5">
              All setup sub-items are completed. Apply a compliance template to start the retainer (client becomes Active),
              defer the decision (client becomes Inactive), or decide later from the client page.
            </p>
            {clientStatus.error && <p className="text-xs text-rose-400 mb-3">{(clientStatus.error as Error).message}</p>}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowFork(false)}
                className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg"
              >
                Later
              </button>
              {canDo('clients.edit') && (
                <button
                  onClick={() => clientStatus.mutate({ clientId: e.clientId, engagementId: e.id })}
                  disabled={clientStatus.isPending}
                  className="text-sm text-muted hover:text-white border border-border hover:border-accent px-3 py-1.5 rounded-lg disabled:opacity-50"
                >
                  Defer
                </button>
              )}
              {canDo('templates.view') && canDo('compliance.edit') && canDo('clients.edit') && (
                <button
                  onClick={() => { setShowFork(false); setApplyFromFork(true); setApplyResult(null); setShowApplyTemplate(true); }}
                  className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
                >
                  Apply template
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {showEdit && (
        <EngagementFormModal
          mode="edit"
          engagement={e}
          onClose={() => setShowEdit(false)}
          onSubmit={(patch, catalog) => update.mutate({ patch, catalog })}
          error={update.error?.message ?? null}
          submitting={update.isPending}
        />
      )}
      {showDelete && (
        <ConfirmModal
          title="Delete engagement"
          message={`Soft-delete engagement "${e.title ?? 'Untitled'}"?`}
          onClose={() => setShowDelete(false)}
          onConfirm={() => remove.mutate()}
          error={remove.error?.message ?? null}
          submitting={remove.isPending}
          confirmText="Delete"
          confirmClass="bg-rose-700 hover:bg-rose-600 text-oncolor"
        />
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <span className="text-xs text-muted uppercase tracking-wide">{label}</span>
      <p className="text-sm text-white mt-1">{value}</p>
    </div>
  );
}

function StatusPill({ pipeline }: { pipeline: string }) {
  const label = ENGAGEMENT_PIPELINE_LABELS[pipeline] ?? pipeline;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium bg-bg-card text-muted border-border">
      {label}
    </span>
  );
}

function DataList<T>({ isLoading, error, items, empty, render }: { isLoading: boolean; error: Error | null; items: T[]; empty: string; render: (item: T) => React.ReactNode }) {
  if (isLoading) return <div className="text-muted p-6 text-center bg-bg-panel border border-border rounded-xl">Loading…</div>;
  if (error) return <div className="text-rose-400 p-6 text-center bg-bg-panel border border-border rounded-xl">{error.message}</div>;
  if (items.length === 0) return <div className="text-muted p-6 text-center bg-bg-panel border border-border rounded-xl">{empty}</div>;
  return <div className="bg-bg-panel border border-border rounded-xl overflow-hidden">{items.map((item, idx) => <div key={idx}>{render(item)}</div>)}</div>;
}

/** OPS 3.3 — one-off sub-items tab: manual lines + business setup catalog spawn. OPS 3.7 step 2: every engagement is a setup engagement, so the catalog picker is always offered to managers; status is a three-state selector. OPS 3.8 step 1: per-item notes are real Note rows via the shared NoteEditorModal (no more save-on-blur); pre-existing `notes` text renders read-only as a legacy note. */
function SubItemsSection({ engagementId, canManage, canAddNote, subItems, isLoading, error, freeZones, onToast, onAllCompleted }: {
  engagementId: string;
  canManage: boolean;
  canAddNote: boolean;
  subItems: EngagementSubItemOutput[];
  isLoading: boolean;
  error: Error | null;
  freeZones: FreeZoneOutput[];
  onToast: (msg: string) => void;
  onAllCompleted?: () => void;
}) {
  const queryClient = useQueryClient();
  const [zoneId, setZoneId] = useState('');
  const [service, setService] = useState('');
  const [newTitle, setNewTitle] = useState('');
  const [itemChecks, setItemChecks] = useState<Record<string, boolean>>({});
  const [noteFor, setNoteFor] = useState<EngagementSubItemOutput | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['engagement-sub-items', engagementId] });

  const applyCatalog = useMutation({
    mutationFn: async ({ catalogItemIds }: { catalogItemIds: string[] }) => {
      const res = await fetch(`/v1/engagements/${engagementId}/sub-items/from-catalog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ freeZoneId: zoneId, service: service || undefined, catalogItemIds }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.error?.message ?? `Failed: ${res.status}`);
      const body = await res.json() as { ok: true; data: { created: number; skipped: number; freeZoneCode: string } };
      return body.data;
    },
    onSuccess: (r) => {
      onToast(`Catalog applied — ${r.created} sub-item(s) added from ${r.freeZoneCode}${r.skipped > 0 ? `, ${r.skipped} already present` : ''}.`);
      invalidate();
    },
  });

  const addManual = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/v1/engagements/${engagementId}/sub-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ title: newTitle.trim() }),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
    },
    onSuccess: () => { setNewTitle(''); invalidate(); },
  });

  const patchItem = useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Record<string, unknown> }) => {
      const res = await fetch(`/v1/engagements/sub-items/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const body = await res.json() as { ok: true; data: EngagementSubItemOutput };
      return body.data;
    },
    onSuccess: (row) => {
      invalidate();
      // Last sub-item flips to Completed → surface the retainer fork.
      if (row.status === 'COMPLETED' && subItems.length > 0 && subItems.every((s) => s.id === row.id || s.status === 'COMPLETED')) {
        onAllCompleted?.();
      }
    },
  });

  const deleteItem = useMutation({
    mutationFn: async (subItemId: string) => {
      const res = await fetch(`/v1/engagements/sub-items/${subItemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
    },
    onSuccess: invalidate,
  });

  /* OPS 3.8 step 1: checklist notes are real Note rows (ENGAGEMENT scope +
   * engagementSubItemId link) — they land in the engagement Notes tab and the
   * client aggregate with the item title as context. */
  const addNote = useMutation({
    mutationFn: async ({ subItemId, input }: { subItemId: string; input: { title?: string; bodyMarkdown: string } }) => {
      const res = await fetch('/v1/notes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ scope: 'ENGAGEMENT', scopeId: engagementId, engagementSubItemId: subItemId, ...input }),
      });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notes', { scope: 'ENGAGEMENT', scopeId: engagementId }] });
      queryClient.invalidateQueries({ queryKey: ['client-notes'] });
    },
  });

  const selectedZone = freeZones.find((z) => z.id === zoneId) ?? null;
  const services = [...new Set((selectedZone?.items ?? []).map((i) => i.service))];
  const poolItems = selectedZone ? (service ? selectedZone.items.filter((i) => i.service === service) : selectedZone.items).filter((i) => i.active) : [];
  const checkedItems = poolItems.filter((i) => itemChecks[i.id] ?? true);
  const STATUS_BADGE: Record<string, string> = { PENDING: 'pill-neutral', IN_PROCESS: 'pill-info', COMPLETED: 'pill-success' };

  return (
    <div className="space-y-4">
      {canManage && (
        <div className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-3">Load from business setup catalog</h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <select value={zoneId} onChange={(e) => { setZoneId(e.target.value); setService(''); setItemChecks({}); }} className="flex-1 bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
              <option value="">Select free zone…</option>
              {freeZones.map((z) => <option key={z.id} value={z.id}>{z.code} — {z.name}</option>)}
            </select>
            <select value={service} onChange={(e) => { setService(e.target.value); setItemChecks({}); }} disabled={!selectedZone} className="flex-1 bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white disabled:opacity-50">
              <option value="">All services in zone</option>
              {services.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button
              onClick={() => applyCatalog.mutate({ catalogItemIds: checkedItems.map((i) => i.id) })}
              disabled={!zoneId || checkedItems.length === 0 || applyCatalog.isPending}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-2 rounded-lg"
            >
              {applyCatalog.isPending ? 'Applying…' : `Apply ${checkedItems.length > 0 ? checkedItems.length : ''} ${checkedItems.length === 1 ? 'item' : 'items'}`}
            </button>
          </div>
          {selectedZone && (
            <div className="mt-2">
              <p className="text-xs text-muted mb-1.5">
                {checkedItems.length} of {poolItems.length} checklist item(s) ticked (already-present items are skipped):
              </p>
              <div className="bg-bg-base border border-border rounded-lg divide-y divide-border/50 max-h-56 overflow-y-auto">
                {poolItems.map((i) => (
                  <label key={i.id} className="px-3 py-1.5 flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={itemChecks[i.id] ?? true}
                      onChange={(e) => setItemChecks((c) => ({ ...c, [i.id]: e.target.checked }))}
                      className="accent-emerald-400 shrink-0"
                    />
                    <span className="text-sm text-white flex-1 min-w-0 truncate">{i.label}</span>
                    <span className="text-xs text-muted shrink-0">{i.service}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {applyCatalog.error && <p className="text-xs text-rose-400 mt-2">{applyCatalog.error.message}</p>}
        </div>
      )}

      {canManage && (
        <div className="bg-bg-panel border border-border rounded-xl p-5">
          <h2 className="text-sm font-medium text-white mb-3">Add sub-item</h2>
          <div className="flex gap-2">
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="One-off work item (e.g. Trade name reservation)" className="flex-1 bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted focus:outline-none focus:border-accent" />
            <button
              onClick={() => addManual.mutate()}
              disabled={!newTitle.trim() || addManual.isPending}
              className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg"
            >
              Add
            </button>
          </div>
        </div>
      )}

      <DataList
        isLoading={isLoading}
        error={error}
        items={subItems}
        empty="No sub-items yet — load the business-setup checklist from the catalog or add items manually."
        render={(s) => (
          <div className="border-b border-border/50 last:border-0 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-white">{s.title}</div>
                <div className="text-xs text-muted">{[s.service, s.freeZoneCode].filter(Boolean).join(' · ')}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <select
                  value={s.status}
                  onChange={(ev) => patchItem.mutate({ id: s.id, patch: { status: ev.target.value } })}
                  disabled={!canManage || patchItem.isPending}
                  className={`px-2 py-0.5 rounded text-xs border border-border bg-bg-base disabled:opacity-60 ${STATUS_BADGE[s.status] ?? 'pill-neutral'}`}
                >
                  {(Object.keys(ENGAGEMENT_SUB_ITEM_STATUS_LABELS) as EngagementSubItemStatus[]).map((st) => (
                    <option key={st} value={st}>{ENGAGEMENT_SUB_ITEM_STATUS_LABELS[st]}</option>
                  ))}
                </select>
                {canAddNote && (
                  <button
                    onClick={() => setNoteFor(s)}
                    title={`Add note to ${s.title}`}
                    aria-label={`Add note to ${s.title}`}
                    className="inline-flex items-center justify-center w-7 h-7 rounded border border-border text-muted hover:text-accent hover:border-accent transition-colors"
                  >
                    <NotebookPen size={14} />
                  </button>
                )}
                {canManage && (
                  <button onClick={() => deleteItem.mutate(s.id)} disabled={deleteItem.isPending} className="text-rose-400 hover:text-rose-300 text-sm" title="Delete sub-item">×</button>
                )}
              </div>
            </div>
            {s.notes && (
              <div className="mt-1.5">
                <span className="text-[10px] uppercase tracking-wide text-muted">Legacy checklist note</span>
                <p className="text-xs text-white whitespace-pre-wrap">{s.notes}</p>
              </div>
            )}
          </div>
        )}
      />
      {noteFor && (
        <NoteEditorModal
          heading="Add checklist note"
          contextLabel={`Checklist item: ${noteFor.title}`}
          onClose={() => setNoteFor(null)}
          onSave={async (input) => { await addNote.mutateAsync({ subItemId: noteFor.id, input }); }}
        />
      )}
    </div>
  );
}
